import type { ApolloError } from '@apollo/client';
import { defaultLocalStorageAdapter } from '@hover/graphql';
import React, { useEffect, useState } from 'react';

import { Alert } from 'src/shared/components';
import { Form as SharedForm } from 'src/shared/components/forms';
import { useAuthenticateMutation } from 'src/shared/graphql/generated';
import { isNetworkError } from 'src/shared/helpers/graphql';
import { baseUrlToRedirectUrl } from 'src/shared/helpers/url';
import {
  useClosure,
  useSegment,
  useSingleSignOn,
  useTranslate,
} from 'src/shared/hooks';
import { TranslationNamespace } from 'src/shared/hooks/useTranslate';
import { identify as identifyAll } from 'src/shared/telemetry';

import { LoginButton } from '../LoginButton';
import type { LoginFormData } from '../types';
import { isPasswordLoginFormData } from '../types';

import { Fields } from './Fields';
import {
  makePasswordValidationSchema,
  makeSingleSignOnValidationSchema,
} from './validationSchema';

const Form = () => {
  const t = useTranslate(TranslationNamespace.LOGIN);

  const [error, setError] = useState<ApolloError | undefined>();
  const { identify, loading: isIdentifying } = useSegment();

  const {
    checkEmail,
    email: singleSignOnEmail = '',
    isActive: isSingleSignOnActive,
    signInUrl,
  } = useSingleSignOn();

  const [
    authenticate,
    { error: authenticationError, loading: isAuthenticating },
  ] = useAuthenticateMutation();

  const initialValues: LoginFormData = {
    email: singleSignOnEmail,
    password: '',
  };

  const onSubmit = useClosure(
    async (formData: LoginFormData) => {
      if (isPasswordLoginFormData(formData)) {
        try {
          const { data } = await authenticate({ variables: formData });

          if (data) {
            const {
              jwt: { accessToken, refreshToken },
              user: { email, id },
            } = data.authenticate;

            try {
              await identifyAll({ userId: id, email });
            } finally {
              void defaultLocalStorageAdapter.setAccessToken(accessToken);
              void defaultLocalStorageAdapter.setRefreshToken(refreshToken);

              window.location.href = baseUrlToRedirectUrl(window.location.href);
            }
          }
        } catch {
          // TODO: expected authentication errors should probably be
          // handled in an Apollo error link at the client level
        }
      } else if (signInUrl) {
        if (singleSignOnEmail) {
          await identify({ email: singleSignOnEmail });
        }

        window.location.href = signInUrl;
      }
    },
    [authenticate, identify, signInUrl, singleSignOnEmail],
  );

  useEffect(() => {
    if (authenticationError) setError(authenticationError);
  }, [authenticationError, setError]);

  useEffect(() => setError(undefined), [isSingleSignOnActive, setError]);

  const validationSchema = isSingleSignOnActive
    ? makeSingleSignOnValidationSchema(t, checkEmail)
    : makePasswordValidationSchema(t);

  const loading = isAuthenticating || isIdentifying;

  const hasErrorAtIdle = error && !loading;
  const hasNetworkError = hasErrorAtIdle && isNetworkError(authenticationError);

  return (
    <SharedForm<LoginFormData>
      initialValues={initialValues}
      onSubmit={onSubmit}
      validationSchema={validationSchema}
    >
      <Fields />
      {hasErrorAtIdle && (
        <Alert alignSelf="stretch" flexDirection="row">
          {hasNetworkError
            ? t('somethingWentWrongTryAgain')
            : t('incorrectUsernameOrPassword')}
        </Alert>
      )}
      <LoginButton isLoading={loading} />
    </SharedForm>
  );
};

export { Form };
